// Nihongo Island マップ定義（プレゼンテーション層）
// 設計根拠：docs/map-design.md, docs/map-implementation-plan.md
// 座標は「思わず次へ進みたくなる」S字ステージ配置。
// 到着から日常へ、生活シーンを一筆書きで巡るゲーム盤面として展開する。

export type AreaId =
  | "airport"
  | "home"
  | "school"
  | "shop"
  | "cafe"
  | "station"
  | "library"
  | "park"
  | "friendhouse"
  | "hospital"
  | "cityoffice"
  | "workplace";

export type Zone = "prologue" | "arrival" | "life";

export interface MapArea {
  id: AreaId;
  nameJa: string;
  nameEn: string;
  nameId?: string;
  lifeCopyJa: string;
  lifeCopyEn: string;
  storyCopyJa: string;
  storyCopyEn: string;
  clearCopyJa: string;
  clearCopyEn: string;
  residentNameJa: string;
  residentNameEn: string;
  residentRoleJa: string;
  residentRoleEn: string;
  residentSpecies: string;
  residentEmoji: string;
  residentGreetingJa: string;
  residentGreetingEn: string;
  residentEmote: "note" | "sparkle" | "heart" | "exclamation" | "question";
  guideCopyJa: string;
  guideCopyEn: string;
  visualMotifs: string[];
  zone: Zone;
  level: "入門" | "N5" | "N4";
  /** マップキャンバス座標（建物中心、px）。MAP_SIZE を基準とする */
  position: { x: number; y: number };
  /** 解放順（0 起点） */
  unlockOrder: number;
  /** ルート接続先（前方向のみ。描画は各辺を1回引く） */
  connectsTo: AreaId[];
  /** 建物スプライトのパス。未配置時は placeholderColor で代用 */
  sprite: string;
  /** 実アート差し替えまでのプレースホルダ矩形色 */
  placeholderColor: string;
}

/** マップキャンバス全体サイズ（px）。パン・ズームの基準座標系 */
export const MAP_SIZE = { width: 1800, height: 1360 };

/**
 * ノードの描画目安（px）。
 * AreaNode は width 170px で、position を円バッジ中心として扱う。
 * ラベルとバッジを含む実表示はおよそ 170 x 140px。
 */
export const NODE_SIZE = { width: 170, height: 140 };

export const MAP_AREAS: MapArea[] = [
  {
    id: "airport",
    nameJa: "関西空港",
    nameEn: "Kansai Airport",
    nameId: "Bandara Kansai",
    lifeCopyJa: "はじめまして、大阪。",
    lifeCopyEn: "Nice to meet you, Osaka.",
    storyCopyJa: "海の上の空港に到着。最初の日本語で、自分の名前と行き先を伝える。",
    storyCopyEn: "You arrive at an airport over the sea. Your first Japanese words tell people who you are and where you are going.",
    clearCopyJa: "日本語で自己紹介できた。島への最初の道が開いた。",
    clearCopyEn: "You introduced yourself in Japanese. The first path into the island opened.",
    residentNameJa: "カモメのキャプテン",
    residentNameEn: "Captain Seagull",
    residentRoleJa: "到着ゲートの案内係",
    residentRoleEn: "Arrival gate guide",
    residentSpecies: "seagull",
    residentEmoji: "🕊️",
    residentGreetingJa: "クルックー！日本語の島へようこそ！",
    residentGreetingEn: "Welcome to Nihongo Island!",
    residentEmote: "sparkle",
    guideCopyJa: "まずは名前から。短くても、ちゃんと伝わります。",
    guideCopyEn: "Start with your name. Even a short phrase can be understood.",
    visualMotifs: ["到着ゲート", "案内板", "連絡橋", "スーツケース"],
    zone: "prologue",
    level: "入門",
    position: { x: 220, y: 1190 },
    unlockOrder: 0,
    connectsTo: ["home"],
    sprite: "/assets/map/area_airport.png",
    placeholderColor: "#7c8ba1",
  },
  {
    id: "home",
    nameJa: "家",
    nameEn: "Home",
    nameId: "Rumah",
    lifeCopyJa: "ここが、わたしのうち。",
    lifeCopyEn: "This is my home now.",
    storyCopyJa: "小さな部屋が、島での拠点になる。持ち物と場所のことばを覚える。",
    storyCopyEn: "A small room becomes your base on the island. You learn words for things and places.",
    clearCopyJa: "部屋にあるものを言えるようになった。ここが自分の場所になった。",
    clearCopyEn: "You can talk about what is in your room. This place feels like yours now.",
    residentNameJa: "コアラの大家さん",
    residentNameEn: "Landlord Koala",
    residentRoleJa: "暮らしを教えてくれる人",
    residentRoleEn: "A neighbor who helps you settle in",
    residentSpecies: "koala",
    residentEmoji: "🐨",
    residentGreetingJa: "のんびり、部屋を整えていこうねぇ",
    residentGreetingEn: "Take your time making yourself at home.",
    residentEmote: "heart",
    guideCopyJa: "部屋のものを言えると、暮らしが少し落ち着きます。",
    guideCopyEn: "When you can name things in your room, life starts to feel calmer.",
    visualMotifs: ["机", "時計", "かばん", "郵便受け"],
    zone: "arrival",
    level: "N5",
    position: { x: 520, y: 1050 },
    unlockOrder: 1,
    connectsTo: ["school"],
    sprite: "/assets/map/area_home.png",
    placeholderColor: "#e8a0b4",
  },
  {
    id: "school",
    nameJa: "学校",
    nameEn: "School",
    nameId: "Sekolah",
    lifeCopyJa: "先生の名前、覚えた。",
    lifeCopyEn: "I know my teacher's name.",
    storyCopyJa: "教室で質問する。場所と時間が分かると、一日の動きが見えてくる。",
    storyCopyEn: "You ask questions in class. Places and times make the school day clear.",
    clearCopyJa: "学校で質問できた。分からないことを聞く力がついた。",
    clearCopyEn: "You asked a question at school. Now you can move forward when you do not understand.",
    residentNameJa: "フクロウ先生",
    residentNameEn: "Professor Owl",
    residentRoleJa: "教室の案内役",
    residentRoleEn: "Classroom guide",
    residentSpecies: "owl",
    residentEmoji: "🦉",
    residentGreetingJa: "ホッホー！知は力なり、何でも聞いてくだされ！",
    residentGreetingEn: "Hoo-hoo! Knowledge is power. Ask anything!",
    residentEmote: "note",
    guideCopyJa: "質問できる人は、どこでも強いです。",
    guideCopyEn: "If you can ask questions, you can keep going anywhere.",
    visualMotifs: ["教室", "黒板", "掲示板", "校門"],
    zone: "arrival",
    level: "N5",
    position: { x: 850, y: 1160 },
    unlockOrder: 2,
    connectsTo: ["shop"],
    sprite: "/assets/map/area_school.png",
    placeholderColor: "#d98c5f",
  },
  {
    id: "shop",
    nameJa: "コンビニ",
    nameEn: "Shop",
    nameId: "Minimarket",
    lifeCopyJa: "近くにコンビニ、見つけた。",
    lifeCopyEn: "Found the konbini nearby.",
    storyCopyJa: "棚の前で、ほしいものを選ぶ。買い物の短い言葉を使う。",
    storyCopyEn: "You choose what you want from the shelves and use short shopping phrases.",
    clearCopyJa: "コンビニで、ほしいものを言えた。",
    clearCopyEn: "You asked for what you wanted at the shop.",
    residentNameJa: "タヌキの店長",
    residentNameEn: "Manager Tanuki",
    residentRoleJa: "レジの案内役",
    residentRoleEn: "Checkout guide",
    residentSpecies: "tanuki",
    residentEmoji: "🦝",
    residentGreetingJa: "いらっしゃいポン！なんでも揃ってるポン！",
    residentGreetingEn: "Welcome pon! Everything you need is here!",
    residentEmote: "sparkle",
    guideCopyJa: "買い物は、短い一言でちゃんと進みます。",
    guideCopyEn: "Shopping works with just a few clear words.",
    visualMotifs: ["レジ", "商品棚", "おにぎり", "飲み物"],
    zone: "arrival",
    level: "N5",
    position: { x: 1190, y: 1000 },
    unlockOrder: 3,
    connectsTo: ["cafe"],
    sprite: "/assets/map/area_shop.png",
    placeholderColor: "#5fb0d9",
  },
  {
    id: "cafe",
    nameJa: "カフェ",
    nameEn: "Cafe",
    nameId: "Kafe",
    lifeCopyJa: "いっしょに行こう、って言えた。",
    lifeCopyEn: "I said 'let's go together.'",
    storyCopyJa: "窓際の席で、だれかを誘う。日本語が関係を作り始める。",
    storyCopyEn: "At a window seat, you invite someone along. Japanese starts building a connection.",
    clearCopyJa: "日本語で友だちを誘えた。",
    clearCopyEn: "You invited a friend in Japanese.",
    residentNameJa: "ネコのバリスタ",
    residentNameEn: "Barista Cat",
    residentRoleJa: "放課後に話せる相手",
    residentRoleEn: "Someone to talk with after class",
    residentSpecies: "cat",
    residentEmoji: "🐱",
    residentGreetingJa: "ニャー、淹れたてのコーヒーでひと息ニャ",
    residentGreetingEn: "Meow, relax with freshly brewed coffee.",
    residentEmote: "heart",
    guideCopyJa: "誘いの言葉は、友だちへの小さな橋です。",
    guideCopyEn: "An invitation is a small bridge toward friendship.",
    visualMotifs: ["メニュー", "カップ", "窓際席", "テーブル"],
    zone: "arrival",
    level: "N5",
    position: { x: 1530, y: 850 },
    unlockOrder: 4,
    connectsTo: ["station"],
    sprite: "/assets/map/area_cafe.png",
    placeholderColor: "#c8a26b",
  },
  {
    id: "station",
    nameJa: "駅",
    nameEn: "Station",
    nameId: "Stasiun",
    lifeCopyJa: "乗り換え、もう迷わない。",
    lifeCopyEn: "No more getting lost.",
    storyCopyJa: "改札を通り、行き先を聞く。移動できると島が広くなる。",
    storyCopyEn: "You pass the gate and ask where to go. When you can travel, the island grows.",
    clearCopyJa: "行き先を聞けた。大阪がぐっと近くなった。",
    clearCopyEn: "You asked for directions. Osaka feels much closer now.",
    residentNameJa: "シバ犬の駅長",
    residentNameEn: "Stationmaster Shiba",
    residentRoleJa: "ホームと路線の案内役",
    residentRoleEn: "Platform and train line guide",
    residentSpecies: "shiba",
    residentEmoji: "🐕",
    residentGreetingJa: "ワン！行き先や乗り換えは任せてワン！",
    residentGreetingEn: "Woof! Leave the train lines to me!",
    residentEmote: "exclamation",
    guideCopyJa: "行き先を言えたら、行ける場所が増えます。",
    guideCopyEn: "When you can say where you are going, more places open up.",
    visualMotifs: ["改札", "路線図", "ホーム", "券売機"],
    zone: "arrival",
    level: "N5",
    position: { x: 1280, y: 610 },
    unlockOrder: 5,
    connectsTo: ["library"],
    sprite: "/assets/map/area_station.png",
    placeholderColor: "#8f9bb3",
  },
  {
    id: "library",
    nameJa: "図書館",
    nameEn: "Library",
    nameId: "Perpustakaan",
    lifeCopyJa: "静かな場所、知ってる。",
    lifeCopyEn: "I know a quiet place.",
    storyCopyJa: "静かな場所で、許可とお願いをする。読める言葉も増えていく。",
    storyCopyEn: "In a quiet place, you ask for permission and make polite requests. More written words become readable.",
    clearCopyJa: "ていねいにお願いできた。静かな場所のことばを覚えた。",
    clearCopyEn: "You made a polite request and learned the language of shared spaces.",
    residentNameJa: "ヒツジの司書",
    residentNameEn: "Librarian Sheep",
    residentRoleJa: "本と静かな場所の案内役",
    residentRoleEn: "Guide to books and quiet spaces",
    residentSpecies: "sheep",
    residentEmoji: "🐑",
    residentGreetingJa: "メェ〜、静かに本を楽しんでいってね",
    residentGreetingEn: "Baa~, please enjoy reading quietly.",
    residentEmote: "note",
    guideCopyJa: "お願いの言葉は、どこでも役に立ちます。",
    guideCopyEn: "Request phrases help everywhere.",
    visualMotifs: ["本棚", "貸出カウンター", "読書席", "カード"],
    zone: "arrival",
    level: "N5",
    position: { x: 940, y: 770 },
    unlockOrder: 6,
    connectsTo: ["park"],
    sprite: "/assets/map/area_library.png",
    placeholderColor: "#7d9e7a",
  },
  {
    id: "park",
    nameJa: "公園",
    nameEn: "Park",
    nameId: "Taman",
    lifeCopyJa: "近所に、知ってる顔ができた。",
    lifeCopyEn: "Familiar faces in the neighborhood.",
    storyCopyJa: "ベンチで週末の話をする。過去のことを話すと、会話が長くなる。",
    storyCopyEn: "You talk about the weekend on a bench. Past tense makes conversations longer.",
    clearCopyJa: "週末のことを話せた。近所の人と少し近くなった。",
    clearCopyEn: "You talked about your weekend and got a little closer to people nearby.",
    residentNameJa: "リスのランナー",
    residentNameEn: "Runner Squirrel",
    residentRoleJa: "公園でよく会う人",
    residentRoleEn: "Someone you often meet at the park",
    residentSpecies: "squirrel",
    residentEmoji: "🐿️",
    residentGreetingJa: "キキッ！今日もいい天気！一緒に走ろうよ！",
    residentGreetingEn: "Chirp! Great weather today! Let's go for a run!",
    residentEmote: "sparkle",
    guideCopyJa: "昨日の話ができると、会話が続きます。",
    guideCopyEn: "When you can talk about yesterday, conversations keep going.",
    visualMotifs: ["ベンチ", "花壇", "掲示板", "噴水"],
    zone: "life",
    level: "N4",
    position: { x: 610, y: 570 },
    unlockOrder: 7,
    connectsTo: ["friendhouse"],
    sprite: "/assets/map/area_park.png",
    placeholderColor: "#7fae5e",
  },
  {
    id: "friendhouse",
    nameJa: "ともだちの家",
    nameEn: "Friend's House",
    nameId: "Rumah Teman",
    lifeCopyJa: "あの子の家、また来たい。",
    lifeCopyEn: "I want to come back here.",
    storyCopyJa: "玄関であいさつし、好きなものや行きたい場所を話す。",
    storyCopyEn: "You greet someone at the door and talk about what you like and where you want to go.",
    clearCopyJa: "好きなものと行きたい場所を言えた。友だちの距離が近くなった。",
    clearCopyEn: "You shared what you like and where you want to go. Friendship feels closer.",
    residentNameJa: "クマのともだち",
    residentNameEn: "Bear Friend",
    residentRoleJa: "家に招いてくれる相手",
    residentRoleEn: "Someone who invites you over",
    residentSpecies: "bear",
    residentEmoji: "🐻",
    residentGreetingJa: "ガオー！…じゃなくて、よく遊びに来てくれたね！",
    residentGreetingEn: "Roar! Just kidding, so glad you came over!",
    residentEmote: "heart",
    guideCopyJa: "好きなものを話すと、自分のことが伝わります。",
    guideCopyEn: "Talking about what you like helps people know you.",
    visualMotifs: ["玄関", "リビング", "写真", "手土産"],
    zone: "life",
    level: "N4",
    position: { x: 330, y: 350 },
    unlockOrder: 8,
    connectsTo: ["hospital"],
    sprite: "/assets/map/area_friendhouse.png",
    placeholderColor: "#dba6a0",
  },
  {
    id: "hospital",
    nameJa: "病院",
    nameEn: "Hospital",
    nameId: "Rumah Sakit",
    lifeCopyJa: "ちゃんと診てもらえた。",
    lifeCopyEn: "I got the care I needed.",
    storyCopyJa: "受付で症状を伝える。不安な時ほど、必要な言葉が力になる。",
    storyCopyEn: "You explain your symptoms at reception. The right words matter most when you feel uneasy.",
    clearCopyJa: "具合の悪さを説明できた。必要な助けを受けられた。",
    clearCopyEn: "You explained how you felt and got the help you needed.",
    residentNameJa: "シカのドクター",
    residentNameEn: "Doctor Deer",
    residentRoleJa: "受付と診察の案内役",
    residentRoleEn: "Reception and care guide",
    residentSpecies: "deer",
    residentEmoji: "🦌",
    residentGreetingJa: "大丈夫ですよ。どこが痛むかゆっくり教えてね",
    residentGreetingEn: "You're safe here. Tell me gently where it hurts.",
    residentEmote: "heart",
    guideCopyJa: "困った時の言葉は、安心につながります。",
    guideCopyEn: "Words for trouble lead to relief.",
    visualMotifs: ["受付", "診察室", "問診票", "薬袋"],
    zone: "life",
    level: "N4",
    position: { x: 740, y: 240 },
    unlockOrder: 9,
    connectsTo: ["cityoffice"],
    sprite: "/assets/map/area_hospital.png",
    placeholderColor: "#d98c8c",
  },
  {
    id: "cityoffice",
    nameJa: "役所",
    nameEn: "City Office",
    nameId: "Kantor Kelurahan",
    lifeCopyJa: "書類、自分でできた。",
    lifeCopyEn: "I handled the paperwork.",
    storyCopyJa: "番号札を取り、理由を説明する。生活の手続きを自分で進める。",
    storyCopyEn: "You take a number and explain your reason. You handle daily paperwork on your own.",
    clearCopyJa: "役所で理由を説明できた。生活の手続きが一つ進んだ。",
    clearCopyEn: "You explained your reason at city office. One more part of life moved forward.",
    residentNameJa: "ペンギンの窓口係",
    residentNameEn: "Clerk Penguin",
    residentRoleJa: "手続きの案内役",
    residentRoleEn: "Paperwork guide",
    residentSpecies: "penguin",
    residentEmoji: "🐧",
    residentGreetingJa: "ペタペタ…書類の手続きはすべてお任せください！",
    residentGreetingEn: "Waddle-waddle... leave all the paperwork to me!",
    residentEmote: "note",
    guideCopyJa: "理由を言えると、手続きが進みます。",
    guideCopyEn: "When you can explain why, paperwork moves forward.",
    visualMotifs: ["窓口", "番号札", "書類", "案内板"],
    zone: "life",
    level: "N4",
    position: { x: 1080, y: 340 },
    unlockOrder: 10,
    connectsTo: ["workplace"],
    sprite: "/assets/map/area_cityoffice.png",
    placeholderColor: "#9b8fb3",
  },
  {
    id: "workplace",
    nameJa: "アルバイト先",
    nameEn: "Workplace",
    nameId: "Tempat Kerja",
    lifeCopyJa: "ここの人たちと、仕事してる。",
    lifeCopyEn: "I work with people here.",
    storyCopyJa: "休憩室で相談し、仕事の報告をする。自分の意見も少しずつ言える。",
    storyCopyEn: "You ask for help in the break room and report about work. Little by little, you can share your opinion.",
    clearCopyJa: "仕事の報告と意見が言えた。島での暮らしが仕事につながった。",
    clearCopyEn: "You gave a work update and shared an opinion. Life on the island now connects to work.",
    residentNameJa: "カワウソ店長",
    residentNameEn: "Otter Boss",
    residentRoleJa: "仕事を教えてくれる人",
    residentRoleEn: "Workplace mentor",
    residentSpecies: "otter",
    residentEmoji: "🦦",
    residentGreetingJa: "よーし！仲間と一緒に元気にがんばろう！",
    residentGreetingEn: "All right! Let's do great teamwork together!",
    residentEmote: "exclamation",
    guideCopyJa: "仕事の言葉は、信頼を作っていきます。",
    guideCopyEn: "Workplace words build trust.",
    visualMotifs: ["制服", "タイムカード", "休憩室", "メモ"],
    zone: "life",
    level: "N4",
    position: { x: 1540, y: 240 },
    unlockOrder: 11,
    connectsTo: [],
    sprite: "/assets/map/area_workplace.png",
    placeholderColor: "#c79b6b",
  },
];

export const AREA_BY_ID: Record<AreaId, MapArea> = Object.fromEntries(
  MAP_AREAS.map((a) => [a.id, a]),
) as Record<AreaId, MapArea>;

/** 描画用ルート辺リスト（connectsTo を展開、各辺1回） */
export const MAP_ROUTES: Array<{ from: AreaId; to: AreaId }> = MAP_AREAS.flatMap(
  (a) => a.connectsTo.map((to) => ({ from: a.id, to })),
);

/**
 * エリアが解放済みかを判定する。
 * unlockedAreaIds は profile.unlocked_areas（API 由来の string[]）をそのまま渡す。
 */
export function isAreaUnlocked(id: AreaId, unlockedAreaIds: string[]): boolean {
  return unlockedAreaIds.includes(id);
}

/** 言語に応じたエリア名取得（ja / id / en） */
export function getAreaName(area: MapArea, lang: string): string {
  if (lang === "ja") return area.nameJa;
  if (lang === "id") return area.nameId ?? area.nameEn;
  return area.nameEn;
}

/** エリアごとの生活風景・代表モチーフ絵文字マッピング */
export const AREA_MOTIF_ICONS: Record<AreaId, string[]> = {
  airport: ["✈️", "🪧", "🧳"],
  home: ["🏠", "🛏️", "📮"],
  school: ["🏫", "🎒", "📖"],
  shop: ["🏪", "🍙", "🧃"],
  cafe: ["☕", "🍰", "🥪"],
  station: ["🚃", "🎫", "🚉"],
  library: ["📚", "🪑", "🖋️"],
  park: ["🌳", "🌸", "🚲"],
  friendhouse: ["🏡", "🍵", "🎮"],
  hospital: ["🏥", "💊", "🩺"],
  cityoffice: ["🏛️", "📋", "🏢"],
  workplace: ["🏢", "💻", "💼"],
};
